import React, { useState, useEffect, useMemo } from 'react';
import { 
  LayoutDashboard, Users, Package, FileText, Settings as SettingsIcon, Plus, Search, Trash2, 
  Save, X, Mail, Phone, DollarSign, ChevronLeft, Filter, Upload, Edit, Download, Database, 
  RefreshCw, Globe, User, CheckCircle, Send, ArrowLeft, ArrowRight, BarChart3, LogOut, Shield, 
  Cloud, FileSpreadsheet, Loader, Copy, Sun, Moon, MessageCircle, FileX, Bell, Clock, 
  AlertCircle, Check, MapPin, Hash, UserCheck, Smartphone, Tag
} from 'lucide-react';
import { THEMES, HOSTINGER_API_URL, CATEGORY_SORT_ORDER, DEFAULT_CATEGORIES, DEFAULT_TERMS, COMPANY_INFO, STATUS_OPTIONS } from '../config/constants';
import { apiCall } from '../services/api';
import BackgroundOrbs from '../components/BackgroundOrbs';
import Card from '../components/Card';
import Button from '../components/Button';
import Badge from '../components/Badge';
import Modal from '../components/Modal';
import { generateCustomerKey, formatCurrency } from '../utils/helpers';

const CustomersView = ({ customers, setCustomers, currentUser, users, isScriptsLoaded, theme }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ name: '', address: '', state: '', country: '', gstin: '', email: '', contactPerson: '', phone: '', personnelEmail: '' });
  const [isSaving, setIsSaving] = useState(false);
  
  const [locationFilter, setLocationFilter] = useState('All');
  const [selectedCustomerIds, setSelectedCustomerIds] = useState([]);
  const [transferTargetUser, setTransferTargetUser] = useState('');

  const visibleCustomers = customers.filter(c => currentUser.role === 'admin' || c.userId === currentUser.id);
  
  const uniqueLocations = useMemo(() => {
      const locs = new Set(visibleCustomers.map(c => c.state).filter(Boolean));
      visibleCustomers.forEach(c => { if(c.country) locs.add(c.country); });
      return ['All', ...Array.from(locs).sort()];
  }, [visibleCustomers]);

  const filteredCustomers = useMemo(() => {
    return visibleCustomers.filter(c => {
        const matchesSearch = c.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
                              (c.contactPerson && c.contactPerson.toLowerCase().includes(searchTerm.toLowerCase()));
        const matchesLocation = locationFilter === 'All' || c.state === locationFilter || c.country === locationFilter;
        return matchesSearch && matchesLocation;
    }).sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [visibleCustomers, searchTerm, locationFilter]);

  const handleSubmit = async (e) => { 
    e.preventDefault(); 
    setIsSaving(true);
    const upperName = (formData.name || "").toUpperCase();
    const cleanFormData = { ...formData, name: upperName };

    let newCustomer;
    let res;
    if (editingId) { 
      newCustomer = { ...cleanFormData, id: editingId, userId: customers.find(c=>c.id===editingId).userId };
      res = await apiCall('save_customer', newCustomer);
    } else { 
      const isDuplicate = customers.some(c => c.name?.toUpperCase() === upperName && c.state === formData.state && c.address === formData.address);
      if (isDuplicate) {
          if(!window.confirm(`A customer named "${upperName}" from this state/address already exists. Save anyway?`)) {
              setIsSaving(false); return;
          }
      }
      newCustomer = { ...cleanFormData, id: Date.now().toString(), userId: currentUser.id };
      res = await apiCall('save_customer', newCustomer);
    }
    
    if (res && res.success) {
      if (editingId) setCustomers(customers.map(c => c.id === editingId ? newCustomer : c)); 
      else setCustomers([...customers, newCustomer]); 
      setIsModalOpen(false); 
      setEditingId(null); 
      setFormData({ name: '', address: '', state: '', country: '', gstin: '', email: '', contactPerson: '', phone: '', personnelEmail: '' }); 
    } else {
      alert("Failed to save customer: " + (res?.message || 'Network Error'));
    }
    setIsSaving(false);
  };

  const handleEdit = (customer) => { setFormData(customer); setEditingId(customer.id); setIsModalOpen(true); };
  const handleDelete = async (id) => { 
    if (window.confirm('Are you sure you want to delete this customer?')) { 
      const res = await apiCall('delete_customer', { id });
      if (res && res.success) {
        setCustomers(customers.filter(c => c.id !== id)); 
      } else {
        alert("Failed to delete customer: " + (res?.message || 'Network Error'));
      }
    } 
  };
  const handleAddNew = () => { setEditingId(null); setFormData({ name: '', address: '', state: '', country: '', gstin: '', email: '', contactPerson: '', phone: '', personnelEmail: '' }); setIsModalOpen(true); };

  const handleImportCustomers = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const existingKeys = new Set(customers.map(c => generateCustomerKey(c)));

    if (file.name.toLowerCase().endsWith('.xml')) {
        const reader = new FileReader();
        reader.onload = async (evt) => {
            try {
                let text = evt.target.result;
                text = text.replace(/&#[0-8];|&#1[12];|&#1[4-9];|&#2[0-9];|&#3[01];/g, ''); 
                
                const parser = new DOMParser();
                const xmlDoc = parser.parseFromString(text, "text/xml");
                const envelope = xmlDoc.getElementsByTagName("ENVELOPE")[0];
                if (!envelope) {
                    alert("Invalid XML format: Missing ENVELOPE tag.");
                    return;
                }

                const children = envelope.children;
                const newCustomers = [];
                let currentCust = null;
                const fileKeys = new Set();

                for (let i = 0; i < children.length; i++) {
                    const node = children[i];
                    if (node.tagName === 'MULTILEDSLNO') {
                        if (currentCust && currentCust.name) {
                            const key = generateCustomerKey(currentCust);
                            if (!existingKeys.has(key) && !fileKeys.has(key)) {
                                newCustomers.push(currentCust);
                                fileKeys.add(key);
                            }
                        }
                        currentCust = {
                             id: Date.now().toString() + Math.random().toString(36).substr(2, 5),
                             userId: currentUser.id,
                             name: '', address: '', state: '', country: '', gstin: '', 
                             email: '', contactPerson: '', phone: '', personnelEmail: '',
                             website: ''
                        };
                    }
                    if (currentCust) {
                        if (node.tagName === 'MULTILEDLEDNAME') currentCust.name = node.textContent?.trim().toUpperCase() || '';
                        if (node.tagName === 'MULTILEDADDRESS') currentCust.address = node.textContent?.trim() || '';
                        if (node.tagName === 'LEDSTATE') currentCust.state = node.textContent?.trim() || '';
                        if (node.tagName === 'LEDMAILINGCOUNTRYNAME') currentCust.country = node.textContent?.trim() || '';
                        if (node.tagName === 'LEDGSTIN') currentCust.gstin = node.textContent?.trim() || '';
                    }
                }
                if (currentCust && currentCust.name) {
                    const key = generateCustomerKey(currentCust);
                    if (!existingKeys.has(key) && !fileKeys.has(key)) {
                        newCustomers.push(currentCust);
                        fileKeys.add(key);
                    }
                }

                if (newCustomers.length > 0) {
                     for (const nc of newCustomers) await apiCall('save_customer', nc);
                     setCustomers(prev => [...prev, ...newCustomers]);
                     alert(`Imported ${newCustomers.length} new customers. Skipped duplicates.`);
                } else {
                    alert("No new unique customers found in XML.");
                }

            } catch (err) {
                console.error("XML Parse error", err);
                alert("Error parsing XML file.");
            }
        };
        reader.readAsText(file);
        return;
    }

    if (!isScriptsLoaded || typeof window.XLSX === 'undefined') {
        alert("Excel processor loading... Please wait a moment.");
        return;
    }

    const reader = new FileReader();
    reader.onload = async (evt) => {
        try {
            const data = evt.target.result;
            const workbook = window.XLSX.read(data, { type: 'array' });
            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];
            const jsonData = window.XLSX.utils.sheet_to_json(worksheet);

            if (jsonData.length === 0) { alert("No data found in file."); return; }

            let importedCount = 0;
            const newCustomers = [];
            const fileKeys = new Set(); 

            for (const row of jsonData) {
                 const companyName = (row['Company Name'] || row['name'] || row['Name'] || '').toString().trim().toUpperCase();
                 
                 if (companyName) {
                     const tempCust = {
                         name: companyName,
                         address: row['Address'] || row['address'] || '',
                         state: row['State'] || row['state'] || '',
                         gstin: row['GSTIN'] || row['gstin'] || ''
                     };

                     const key = generateCustomerKey(tempCust);
                     
                     if (existingKeys.has(key) || fileKeys.has(key)) {
                         continue; 
                     }
                     
                     const newCustomer = {
                         id: Date.now().toString() + Math.random().toString(36).substr(2, 5),
                         userId: currentUser.id, 
                         name: companyName,
                         contactPerson: row['Contact Person'] || row['contactPerson'] || '',
                         email: row['Email'] || row['email'] || '',
                         personnelEmail: row['Personnel Email'] || row['personnelEmail'] || '',
                         phone: row['Phone'] || row['phone'] || '',
                         website: row['Website'] || row['website'] || '',
                         address: tempCust.address,
                         state: tempCust.state,
                         country: row['Country'] || row['country'] || '',
                         gstin: tempCust.gstin
                     };
                     await apiCall('save_customer', newCustomer);
                     newCustomers.push(newCustomer);
                     fileKeys.add(key);
                     importedCount++;
                 }
            }
            if (newCustomers.length > 0) {
                setCustomers(prev => [...prev, ...newCustomers]);
                alert(`Successfully imported ${importedCount} customers. Skipped duplicates.`);
            } else { alert("No new unique customers found."); }
        } catch (err) { alert("Error parsing Excel file."); console.error(err); }
    };
    reader.readAsArrayBuffer(file);
    e.target.value = null;
  };

  const handleSelectCustomer = (id) => {
    setSelectedCustomerIds(prev => {
        if (prev.includes(id)) return prev.filter(cid => cid !== id);
        return [...prev, id];
    });
  };

  const handleSelectAll = (e) => {
      if (e.target.checked) {
          setSelectedCustomerIds(filteredCustomers.map(c => c.id));
      } else {
          setSelectedCustomerIds([]);
      }
  };

  const handleBulkTransfer = async () => {
      if (!transferTargetUser) { alert("Please select a user to transfer to."); return; }
      if (selectedCustomerIds.length === 0) { alert("No customers selected."); return; }
      
      if (window.confirm(`Transfer ${selectedCustomerIds.length} customers to selected user?`)) {
          const targetUserCustomers = customers.filter(c => c.userId === transferTargetUser);
          const targetKeys = new Set(targetUserCustomers.map(c => generateCustomerKey(c)));
          let transferredCount = 0;
          let skippedCount = 0;

            let updatedCustomers = [...customers];
            for (const custId of selectedCustomerIds) {
                const customer = updatedCustomers.find(c => c.id === custId);
                if (customer) {
                    const key = generateCustomerKey(customer);
                    if (targetKeys.has(key)) {
                        skippedCount++; 
                    } else {
                        const updated = { ...customer, userId: transferTargetUser };
                        const res = await apiCall('save_customer', updated);
                        if (res && res.success) {
                            transferredCount++;
                            Object.assign(customer, updated);
                        } else {
                            skippedCount++;
                        }
                    }
                }
            }
            
            setCustomers(updatedCustomers); 
          setSelectedCustomerIds([]);
          setTransferTargetUser('');
          alert(`Transfer complete. Transferred: ${transferredCount}, Skipped (Duplicate): ${skippedCount}`);
      }
  };
  
  const handleBulkDelete = async () => {
    if (selectedCustomerIds.length === 0) return;
    if (window.confirm(`Are you sure you want to PERMANENTLY delete ${selectedCustomerIds.length} selected customers?`)) {
        let deletedIds = new Set();
        
        for (const id of selectedCustomerIds) {
            const res = await apiCall('delete_customer', { id });
            if (res && res.success) deletedIds.add(id);
        }

        if (deletedIds.size > 0) {
            setCustomers(prev => prev.filter(c => !deletedIds.has(c.id)));
            setSelectedCustomerIds([]);
            alert(`${deletedIds.size} selected customers deleted.`);
        } else {
            alert("Failed to delete selected customers.");
        }
    }
  };

  const handleRemoveDuplicates = async () => {
      if (!window.confirm("This will scan ALL customers and remove duplicates based on Name, GST, State & Address. Are you sure?")) return;
      
      const seenKeys = new Set();
      const uniqueCustomers = [];
      const duplicatesToRemove = [];

      customers.forEach(c => {
          const key = generateCustomerKey(c);
          if (seenKeys.has(key)) {
              duplicatesToRemove.push(c.id);
          } else {
              seenKeys.add(key);
              uniqueCustomers.push(c);
          }
      });

      if (duplicatesToRemove.length === 0) {
          alert("No duplicates found.");
          return;
      }

      if (window.confirm(`Found ${duplicatesToRemove.length} duplicates. Delete them permanently?`)) {
          let successfullyDeletedIds = new Set();
          for (const id of duplicatesToRemove) {
              const res = await apiCall('delete_customer', { id });
              if (res && res.success) successfullyDeletedIds.add(id);
          }
          setCustomers(prev => prev.filter(c => !successfullyDeletedIds.has(c.id)));
          alert(`Successfully removed ${successfullyDeletedIds.size} duplicates.`);
      }
  };

  return (
    <div className="p-8 space-y-8 animate-in fade-in duration-500">
      <div className="flex flex-col md:flex-row justify-between md:items-center gap-4">
        <h2 className={`text-4xl font-black ${THEMES[theme].heading}`}>Customers</h2>
        <div className="flex gap-2">
          {currentUser.role === 'admin' && (
            <>
                <button onClick={handleRemoveDuplicates} className="flex items-center gap-2 px-4 py-3 bg-red-100/50 text-red-600 hover:bg-red-200/50 rounded-2xl text-sm font-bold transition-all border border-red-200 backdrop-blur-md">
                    <FileX size={16} /> Cleanup Duplicates
                </button>
                <label className="cursor-pointer flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white hover:shadow-lg hover:-translate-y-0.5 rounded-2xl text-sm font-bold transition-all shadow-emerald-500/20">
                <Download size={16} /> <span>Import</span>
                <input type="file" accept=".xlsx, .xls, .csv, .xml" className="hidden" onChange={handleImportCustomers} disabled={!isScriptsLoaded} />
                </label>
            </>
          )}
          <Button variant="secondary" onClick={handleAddNew} theme={theme}><Plus size={16} /> Add Customer</Button>
        </div>
      </div>
      
      <div className={`flex flex-col md:flex-row gap-4 items-center p-2 rounded-[2rem] border bg-white/30 backdrop-blur-xl ${theme==='dark'?'border-slate-800':'border-white/50'}`}>
          <div className="relative flex-1 w-full"><Search className="absolute left-4 top-3.5 text-slate-400" size={18} /><input type="text" placeholder="Search customers..." className={`pl-12 pr-4 py-3 rounded-2xl text-sm outline-none bg-transparent w-full font-medium ${theme==='dark'?'text-white placeholder:text-slate-600':'text-slate-700 placeholder:text-slate-400'}`} value={searchTerm || ''} onChange={(e) => setSearchTerm(e.target.value)} /></div>
          <div className="flex items-center gap-2 pr-2">
            <span className={`text-sm font-bold ${THEMES[theme].textMuted}`}>Location:</span>
            <select className={`p-2 rounded-xl text-sm outline-none bg-white/50 border border-white/40 min-w-[150px] font-medium text-slate-700`} value={locationFilter} onChange={(e) => setLocationFilter(e.target.value)}>
                {uniqueLocations.map(loc => <option key={loc} value={loc}>{loc}</option>)}
            </select>
          </div>
      </div>

      <Card className="overflow-hidden" theme={theme}>
        <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className={`border-b ${theme==='dark'?'border-slate-800':'border-white/30'} ${THEMES[theme].tableHeader}`}>
              <tr>
                  {currentUser.role === 'admin' && (
                      <th className="p-6 w-10">
                          <input type="checkbox" onChange={handleSelectAll} checked={filteredCustomers.length > 0 && selectedCustomerIds.length === filteredCustomers.length} className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"/>
                      </th>
                  )}
                  <th className="p-6">Company Name</th>
                  <th className="p-6">Contact Person</th>
                  <th className="p-6">Contact Info</th>
                  <th className="p-6">Location</th>
                  {currentUser.role === 'admin' && <th className="p-6">Owner</th>}
                  <th className="p-6 text-right">Actions</th>
              </tr>
          </thead>
          <tbody className={`divide-y ${theme==='dark'?'divide-slate-800':'divide-slate-100/50'}`}>
            {filteredCustomers.length > 0 ? filteredCustomers.map(customer => { 
              const owner = users?.find(u => u.id === customer.userId); 
              return (
                <tr key={customer.id} className={`${THEMES[theme].tableRow} group`}>
                  {currentUser.role === 'admin' && (
                      <td className="p-6">
                          <input type="checkbox" checked={selectedCustomerIds.includes(customer.id)} onChange={() => handleSelectCustomer(customer.id)} className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"/>
                      </td>
                  )}
                  <td className={`p-6 font-bold ${THEMES[theme].text}`}>{customer.name.toUpperCase()}</td>
                  <td className={`p-6 ${THEMES[theme].textMuted}`}>{customer.contactPerson}</td>
                  <td className={`p-6 ${THEMES[theme].textMuted}`}><div className="flex flex-col gap-1 text-xs"><span className="flex items-center gap-1"><Mail size={12}/> {customer.email}</span><span className="flex items-center gap-1"><Phone size={12}/> {customer.phone}</span></div></td>
                  <td className={`p-6 ${THEMES[theme].textMuted}`}>{customer.state}, {customer.country}</td>
                  {currentUser.role === 'admin' && (<td className="p-6 text-xs text-slate-500"><span className={`px-2 py-1 rounded ${theme==='dark'?'bg-slate-800':'bg-slate-100'}`}>{owner?.name || 'Unknown'}</span></td>)}
                  <td className="p-6 text-right"><div className="flex justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity"><button onClick={() => handleEdit(customer)} className="p-2 hover:bg-blue-50 text-slate-400 hover:text-blue-600 rounded-lg transition-colors"><Edit size={16}/></button><button onClick={() => handleDelete(customer.id)} className="p-2 hover:bg-red-50 text-slate-400 hover:text-red-600 rounded-lg transition-colors"><Trash2 size={16}/></button></div></td>
                </tr>
              )
            }) : (<tr><td colSpan={currentUser.role === 'admin' ? 7 : 6} className={`p-10 text-center ${THEMES[theme].textMuted}`}>No customers found.</td></tr>)}
          </tbody>
        </table>
        </div>
      </Card>
      
      {currentUser.role === 'admin' && selectedCustomerIds.length > 0 && (
          <div className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-[1000] p-4 rounded-2xl shadow-2xl border flex items-center gap-4 animate-in slide-in-from-bottom-10 ${theme==='dark' ? 'bg-slate-900 border-slate-700' : 'bg-white/90 backdrop-blur-xl border-white/50'}`}>
              <div className={`font-bold ${THEMES[theme].text}`}>{selectedCustomerIds.length} Selected</div>
              <div className="h-6 w-px bg-slate-300 dark:bg-slate-700"></div>
              <div className="flex items-center gap-2">
                  <span className={`text-sm ${THEMES[theme].textMuted}`}>Transfer to:</span>
                  <select 
                      className={`p-2 rounded-lg text-sm outline-none border ${theme==='dark'?'bg-slate-800 border-slate-700 text-white':'bg-slate-50 border-slate-200 text-slate-900'}`}
                      value={transferTargetUser}
                      onChange={(e) => setTransferTargetUser(e.target.value)}
                  >
                      <option value="">Select User...</option>
                      {users.map(u => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
                  </select>
                  <Button onClick={handleBulkTransfer} theme={theme} className="bg-blue-600 hover:bg-blue-700 text-white border-none">
                      Transfer <ArrowRight size={16} />
                  </Button>
                  <div className="h-6 w-px bg-slate-300 dark:bg-slate-700 mx-2"></div>
                  <Button onClick={handleBulkDelete} theme={theme} className="bg-red-600 hover:bg-red-700 text-white border-none">
                      Delete <Trash2 size={16} />
                  </Button>
                  <button onClick={() => setSelectedCustomerIds([])} className="p-2 text-slate-400 hover:text-slate-600"><X size={16}/></button>
              </div>
          </div>
      )}

      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={editingId ? "Edit Customer" : "Add New Customer"} theme={theme} className="max-width-md">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div><label className={`block text-xs font-bold uppercase tracking-wider mb-2 ${THEMES[theme].textMuted} ml-1`}>Customer Name</label><input required type="text" className={`w-full p-4 rounded-xl outline-none ${THEMES[theme].input}`} value={formData.name || ''} onChange={e => setFormData({...formData, name: e.target.value.toUpperCase()})} /></div>
          <div><label className={`block text-xs font-bold uppercase tracking-wider mb-2 ${THEMES[theme].textMuted} ml-1`}>Address</label><textarea rows="2" className={`w-full p-4 rounded-xl outline-none ${THEMES[theme].input}`} value={formData.address || ''} onChange={e => setFormData({...formData, address: e.target.value})} /></div>
          <div className="grid grid-cols-2 gap-4"><div><label className={`block text-xs font-bold uppercase tracking-wider mb-2 ${THEMES[theme].textMuted} ml-1`}>State</label><input type="text" className={`w-full p-4 rounded-xl outline-none ${THEMES[theme].input}`} value={formData.state || ''} onChange={e => setFormData({...formData, state: e.target.value})} /></div><div><label className={`block text-xs font-bold uppercase tracking-wider mb-2 ${THEMES[theme].textMuted} ml-1`}>Country</label><input type="text" className={`w-full p-4 rounded-xl outline-none ${THEMES[theme].input}`} value={formData.country || ''} onChange={e => setFormData({...formData, country: e.target.value})} /></div></div>
          <div><label className={`block text-xs font-bold uppercase tracking-wider mb-2 ${THEMES[theme].textMuted} ml-1`}>GSTN / Reg. No.</label><input type="text" className={`w-full p-4 rounded-xl outline-none ${THEMES[theme].input}`} value={formData.gstin || ''} onChange={e => setFormData({...formData, gstin: e.target.value})} /></div>
          <div><label className={`block text-xs font-bold uppercase tracking-wider mb-2 ${THEMES[theme].textMuted} ml-1`}>Email (Company)</label><input type="email" className={`w-full p-4 rounded-xl outline-none ${THEMES[theme].input}`} value={formData.email || ''} onChange={e => setFormData({...formData, email: e.target.value})} /></div>
          <div><label className={`block text-xs font-bold uppercase tracking-wider mb-2 ${THEMES[theme].textMuted} ml-1`}>Contact Person</label><input type="text" className={`w-full p-4 rounded-xl outline-none ${THEMES[theme].input}`} value={formData.contactPerson || ''} onChange={e => setFormData({...formData, contactPerson: e.target.value})} /></div>
          <div><label className={`block text-xs font-bold uppercase tracking-wider mb-2 ${THEMES[theme].textMuted} ml-1`}>Phone Number</label><input type="text" className={`w-full p-4 rounded-xl outline-none ${THEMES[theme].input}`} value={formData.phone || ''} onChange={e => setFormData({...formData, phone: e.target.value})} /></div>
          <div><label className={`block text-xs font-bold uppercase tracking-wider mb-2 ${THEMES[theme].textMuted} ml-1`}>Personnel Email</label><input type="email" className={`w-full p-4 rounded-xl outline-none ${THEMES[theme].input}`} value={formData.personnelEmail || ''} onChange={e => setFormData({...formData, personnelEmail: e.target.value})} /></div>
          <div className="pt-4 flex justify-end gap-3"><Button variant="ghost" onClick={() => setIsModalOpen(false)} theme={theme}>Cancel</Button><Button variant="primary" type="submit" disabled={isSaving} theme={theme}>{isSaving ? 'Saving...' : 'Save Customer'}</Button></div>
        </form>
      </Modal>
    </div>
  );
};

export default CustomersView;
